import type {TEndpoint, THTTPVerb, TPathPatternToPathPieces} from './types'

const PATH_PARAM = /:([A-Za-z0-9_]+)/g

const buildPath = <TPattern extends string>(
  pattern: TPattern,
  pathPieces: TPathPatternToPathPieces<TPattern>
): string =>
  pattern.replace(PATH_PARAM, (_match, name: string) => {
    const value = (pathPieces as Record<string, string | undefined>)[name]

    if (value === undefined) {
      // Unreachable via the types; catches untyped data, which would otherwise fetch a
      // literal `/game/:gameId/...` and 404 far from the cause.
      throw new Error(
        `Missing path piece '${name}' for endpoint pattern '${pattern}'`
      )
    }

    // Substituted raw, not percent-encoded. Path pieces are UUIDs and short enums; a value
    // that genuinely needs escaping should be encoded by the caller, which knows what it
    // holds. Encoding here would also mangle `BaseApiClient.genericizedPathWithHTTPVerb`,
    // which builds its error-grouping key by passing `:name` placeholders through this.
    return value
  })

/**
 * Declares one endpoint:
 *
 *     defineEndpoint<TSignUpParams, EmptyObject>()('POST', '/signup')
 *
 * Curried because TypeScript has no partial inference: one call taking both would force
 * `TPattern` to be written by hand, and widening it to `string` costs the path-piece names
 * and Hono's param inference.
 */
export const defineEndpoint =
  <TRequest, TResponse>() =>
  <TVerb extends THTTPVerb, TPattern extends string>(
    httpVerb: TVerb,
    pattern: TPattern
  ): TEndpoint<TVerb, TPattern, TRequest, TResponse> => ({
    httpVerb,
    pattern,
    path: pathPieces => buildPath(pattern, pathPieces),
  })
